import { Button, Flex, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useAntTheme } from "hooks/useAntTheme";
import { AudioWavePlayback } from "./AudioWavePlayback";
import { formatTime } from "../../hooks/useVoiceCloning";

type AudioSampleWaveProps = {
  audio: HTMLAudioElement | Blob;
  children?: React.ReactNode;
  play: boolean;
  setPlay: React.Dispatch<React.SetStateAction<boolean>>;
  showTime?: boolean;
};

export const AudioSampleWave: React.FC<AudioSampleWaveProps> = ({
  audio,
  children,
  play,
  setPlay,
  showTime = true,
}) => {
  const { theme } = useAntTheme();
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animationRef = useRef<number>(0);

  useEffect(() => {
    (async (play: boolean) => {
      const audioEl = audioRef.current;
      if (!audioEl) return;

      if (play) {
        if (currentTime >= duration) {
          audioEl.currentTime = 0;
          setCurrentTime(0);
        }
        await audioEl.play();
        setPlaying(true);
      } else {
        audioEl.pause();
        setPlaying(false);
      }
    })(play).catch((error) => console.error("Error toggling play:", error));
  }, [play, currentTime, duration]);

  // Initialize audio element
  useEffect(() => {
    if (audio instanceof Blob) {
      audioRef.current = new Audio(URL.createObjectURL(audio));
    } else {
      audioRef.current = audio;
    }

    const audioEl = audioRef.current;

    const handleLoadedMetadata = () => {
      if (audioEl && !Number.isNaN(audioEl.duration)) {
        setDuration(audioEl.duration);
      }
    };

    const handleEnded = () => {
      setPlaying(false);
      setPlay(false);
      setCurrentTime(0);
      cancelAnimationFrame(animationRef.current);
    };

    audioEl.addEventListener("loadedmetadata", handleLoadedMetadata);
    audioEl.addEventListener("ended", handleEnded);

    // If already loaded
    if (audioEl.duration && !Number.isNaN(audioEl.duration)) {
      setDuration(audioEl.duration);
    }

    return () => {
      audioEl.pause();
      audioEl.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audioEl.removeEventListener("ended", handleEnded);
      cancelAnimationFrame(animationRef.current);
    };
  }, [audio, setPlay]);

  // Update currentTime during playback
  useEffect(() => {
    if (playing) {
      const updateTime = () => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
        }
        animationRef.current = requestAnimationFrame(updateTime);
      };
      animationRef.current = requestAnimationFrame(updateTime);
    } else {
      cancelAnimationFrame(animationRef.current);
    }

    return () => cancelAnimationFrame(animationRef.current);
  }, [playing]);

  const handleWaveClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || duration === 0) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = clickX / rect.width;
    const newTime = percent * duration;

    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  return (
    <Flex vertical gap={8} align="center" style={{ width: "100%" }}>
      {children}
      <Flex gap={4} style={{ width: "100%" }} align="center">
        {showTime && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {formatTime(Math.floor(currentTime))}
          </Typography.Text>
        )}
        <Button
          type="text"
          onClick={handleWaveClick}
          style={{ flex: 1, backgroundColor: "transparent" }}
        >
          <AudioWavePlayback
            audio={audio}
            currentTime={currentTime}
            barColor={theme.colorPrimary}
            paleColor={theme.colorFillSecondary}
            height={30}
          />
        </Button>
        {showTime && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {formatTime(Math.floor(duration))}
          </Typography.Text>
        )}
      </Flex>
    </Flex>
  );
};
